import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./admin.scss";
import { withUser } from "hoc/withUser";
import UserMenu from "components/UserMenu/UserMenu";
import { getAuthUser } from "redux/selectors/getUser";
import Logo from "components/Logo/Logo";
import {
  MdSupervisorAccount,
  MdOutlineGroups,
  MdOutlineModeComment,
  MdLogout,
  MdAirplaneTicket,
  MdSettings,
  MdOutlineAirplaneTicket,
  MdLocalActivity,
  MdHotel,
  MdPayments,
  MdBarChart,
  MdOutlineNotifications,
  MdShoppingCart,
  MdBookmarks,
  MdCode,
  MdIntegrationInstructions,
  MdChat,
  MdInsights,
  MdOutlineConfirmationNumber,
  MdOutlineMap,
  MdOutlineAssignment,
  MdOutlineLocalOffer,
  MdOutlineLoyalty,
  MdOutlineSell,
  MdOutlineMonetizationOn,
  MdTrain,
  MdInbox,
  MdLuggage,
  MdQrCodeScanner,
  MdLocationOn,
  MdViewModule,
  MdDirectionsRailway,
} from "react-icons/md";
import { useEffect, useRef } from "react";
import { Alert, Box, Button, Collapse, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { FaArrowLeft, FaArrowRight, FaBars, FaFire, FaPlus } from "react-icons/fa";
import CurrencyRates from "components/CurrencyRates";
import { getAgents } from "redux/selectors/getAgents";
import { useGetOrg } from "hooks/useGetOrg";
import { useGetAIAgents } from "hooks/useGetAIAgents";
import { setDataAction } from "redux/actions/agentsActions";
import { updateSidebarAction } from "redux/reducers/appLayout";
import { useGetSubscription } from "hooks/useGetSubscription";
import { useGetPlanUsage } from "hooks/useGetPlanUsage";


const AdminLayout = () => {
  const authUser = useSelector(getAuthUser);
  const agentsState = useSelector(getAgents)
  const sidebar = useRef(null);
  const burger = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarOpen = useSelector((state) => state.app.sidebar.open);
  const dispatch = useDispatch();

  const { data: org, isLoading: isOrgLoading } = useGetOrg(localStorage.getItem('orgID'));

  const { data: agentsData, isLoading: isAgentsLoading, error: agentsError } = useGetAIAgents();

  const { data: subscriptionData, isLoading: isSubscriptionLoading, error: subscriptionError, isFetched: isSubscriptionFetched } = useGetSubscription(localStorage.getItem('orgID'));

  useEffect(() => {
    dispatch(setDataAction(agentsData?.rows));
  }, [agentsData]);

  // Лимит ИИ-ответов за месяц исчерпан — ИИ-агент молчит, клиентам отвечают менеджеры
  const { data: planUsage } = useGetPlanUsage(localStorage.getItem('orgID'));
  const aiUsage = planUsage?.find((u) => u.code === "ai_requests_per_month");
  const isAILimitReached = aiUsage && aiUsage.limit !== null && aiUsage.used >= aiUsage.limit;

  // Демо-доступ: сколько дней пробного периода осталось (округляем вверх)
  const trialDaysLeft = subscriptionData?.status === "TRIALING"
    ? Math.ceil((new Date(subscriptionData.currentPeriodEnd).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : 0;

  const adminMenu = [
    {
      icon: MdBarChart,
      path: "stats",
      label: "Аналитика",
    },
    {
      icon: MdInbox,
      path: "inbox",
      label: "Входящие",
    },
    {
      icon: MdSupervisorAccount,
      path: "customers",
      label: 'Управление клиентами',
      children: [
        {
          icon: MdSupervisorAccount,
          path: "customers/all",
          label: "Клиенты",
        },
        {
          icon: MdSupervisorAccount,
          path: "customers/passport",
          label: "Просроченные паспорта",
        },
        {
          icon: MdSupervisorAccount,
          path: "customers/visas",
          label: "Просроченные визы",
        },
        {
          icon: MdSupervisorAccount,
          path: "customers/debtors",
          label: "Реестр должникиов",
        },
      ]
    },
    // Chats
    {
      icon: MdChat,
      path: "chats",
      label: "Чаты",
    },
    {
      icon: MdOutlineMonetizationOn,
      path: "leads",
      label: "Сделки",
    },
    {
      icon: MdOutlineConfirmationNumber,
      path: "tickets",
      label: "Обращения",
      children: [
        {
          icon: MdOutlineConfirmationNumber,
          path: "tickets/list",
          label: "Обращения",
        },
        // Карта обращений по филиалам
        {
          icon: MdOutlineMap,
          path: "tickets/map",
          label: "Карта",
        },
        // Темы обращений
        {
          icon: MdOutlineConfirmationNumber,
          path: "tickets/categories",
          label: "Тематика",
        },
        // Шаблоны ответов (canned replies)
        {
          icon: MdOutlineConfirmationNumber,
          path: "tickets/templates",
          label: "Шаблоны ответов",
        },
      ]
    },
    {
      icon: MdOutlineAssignment,
      path: "tasks",
      label: "Задачи",
    },
    {
      icon: MdLocalActivity,
      path: "tour-packages",
      label: "Управление турпакеты",
      children: [
        {
          icon: MdLocalActivity,
          path: "tour-packages/list",
          label: "Турпакеты",
        },
        {
          icon: MdHotel,
          path: "tour-packages/hotels",
          label: "Отели",
        },
        {
          icon: MdOutlineAirplaneTicket,
          path: "tour-packages/airlines",
          label: "Авиакомпании",
        },
        {
          icon: MdAirplaneTicket,
          path: "tour-packages/flights",
          label: "Расписание рейсов",
        },
        {
          icon: MdBookmarks,
          path: "tour-packages/attributes",
          label: "Атрибуты турпакета",
        },
        {
          icon: MdBookmarks,
          path: "tour-packages/tour-schedules",
          label: "Расписание туров",
        },
        {
          icon: MdBookmarks,
          path: "tour-packages/tourpackage-categories",
          label: "Категории турпакетов",
        },
      ]
    },
    {
      icon: MdPayments,
      path: "sub-orders",
      label: "Управление заказами",
      children: [
        {
          icon: MdPayments,
          path: "sub-orders/list",
          label: "Заказы",
        },
        {
          icon: MdPayments,
          path: "sub-orders/payments",
          label: "История платежей",
        },
        {
          icon: MdPayments,
          path: "sub-orders/unpaid-clients",
          label: "Графа должников",
        },
      ]
    },
    {
      icon: MdAirplaneTicket,
      path: "group-trips",
      label: "Поездки"
    },
  ];


  const baggageMenu = [
    {
      icon: MdLuggage,
      path: "baggages",
      label: "Управление отправками",
      children: [
        {
          icon: MdTrain,
          path: "baggages/baggage-trips",
          label: "Отправки",
        },
        {
          icon: MdInbox,
          path: "baggages/arrivals",
          label: "Прибывшие",
        },
        {
          icon: MdQrCodeScanner,
          path: "baggages/baggage-scan",
          label: "Сканирование",
        },
        {
          icon: MdLocationOn,
          path: "baggages/stations",
          label: "Станции",
        },
        {
          icon: MdViewModule,
          path: "baggages/wagons",
          label: "Вагоны",
        },
        {
          icon: MdDirectionsRailway,
          path: "baggages/runs",
          label: "Рейсы",
        },
        {
          icon: MdSupervisorAccount,
          path: "baggages/counterparties",
          label: "Контрагенты",
        },
        {
          icon: MdPayments,
          path: "baggages/baggage-tariffs",
          label: "Тарифы",
        },
        {
          icon: MdInsights,
          path: "baggages/stats",
          label: "Статистика",
        },
      ],
    },
  ]

  const aiAgentsMenu = [
    {
      icon: FaFire,
      path: "ai-agents",
      label: "ИИ-агенты",
      buttons: [
        {
          icon: FaPlus,
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            navigate("/admin/ai-agents/add");
          },
        },
      ],
      children: agentsState?.length > 0 ? agentsState.map((a => {
        return {
          icon: MdPayments,
          path: `ai-agents/${a.id}/settings`,
          label: a.name,
          children: [
            {
              icon: MdSettings,
              path: `ai-agents/${a.id}/settings`,
              label: "Настройки",
            },
            {
              icon: MdBookmarks,
              path: `ai-agents/${a.id}/prompts`,
              label: "Промтинг",
              disabled: authUser?.phone == "77773001994"
            },
            {
              icon: MdCode,
              path: `ai-agents/${a.id}/functions`,
              label: "Функции",
              disabled: authUser?.phone == "77773001994"
            },
            {
              icon: MdBookmarks,
              path: `ai-agents/${a.id}/knowledge-documents`,
              label: "База знаний",
              disabled: authUser?.phone == "77773001994"
            },
            {
              icon: MdChat,
              path: `ai-agents/${a.id}/dialogs`,
              label: "Тестовая среда",
            },
            {
              icon: MdBarChart,
              path: `ai-agents/${a.id}/stats`,
              label: "Статистика",
            },
            {
              icon: MdOutlineModeComment,
              path: `ai-agents/${a.id}/comments`,
              label: "Комментарии",
            },
          ]
        }
      })) : []
    },
  ]

  const marketplaceMenu = [
    {
      icon: MdShoppingCart,
      path: "products",
      label: "Управление товарами",
      children: [
        {
          icon: MdLocalActivity,
          path: "products/list",
          label: "Товары",
        },
        {
          icon: MdLocalActivity,
          path: "products/warehouses",
          label: "Точки продаж",
        },
        {
          icon: MdLocalActivity,
          path: "products/sub-orders",
          label: "Заказы",
        },
        {
          icon: MdLocalActivity,
          path: "products/returns",
          label: "Возвраты",
        },
      ]
    },
  ]

  const advancedAdminMenu = [
    {
      icon: MdSettings,
      path: "orgs/:id/edit".replace(':id', localStorage.getItem('orgID')),
      label: "Настройки",
    },
    {
      icon: MdSupervisorAccount,
      path: "members",
      label: "Сотрудники",
    },
    {
      icon: MdSupervisorAccount,
      path: "branches",
      label: "Филиалы",
    },
    {
      icon: MdTrain,
      path: "trains",
      label: "Поезда",
    },

    {
      icon: MdSupervisorAccount,
      path: "roles",
      label: "Роли",
    },
    {
      icon: MdIntegrationInstructions,
      path: "integrations",
      label: "Интеграции",
    },
    // Оплата и Тарифы
    {
      icon: MdPayments,
      path: "plans",
      label: "Оплата и Тарифы",
    },
    {
      icon: MdSettings,
      path: 'faq',
      label: "Вопросы-Ответы",
    },
    {
      icon: MdSettings,
      path: 'locations',
      label: "Топ локаций",
    },
    {
      icon: MdOutlineGroups,
      path: "invoices",
      label: "Счет на оплату",
    },
    // {
    //   icon: MdSupervisorAccount,
    //   path: "whatsapp/connect",
    //   label: "Whatsapp Интергация",
    // },
    {
      icon: MdOutlineNotifications,
      path: "broadcasts",
      label: "Массовая рассылки",
    },
    {
      icon: MdOutlineModeComment,
      path: "reviews",
      label: "Обратная связь",
    },
    {
      icon: MdLogout,
      path: "/auth/logout",
      label: "Выйти",
    },
  ];

  // «Ответы клиентам»: где общаемся (каналы) и кто отвечает — чат-бот по сценариям или ИИ-агент.
  // Разница объясняется на страницах и в онбординге (components/ReplyModes).
  const replyMenu = [
    {
      icon: MdIntegrationInstructions,
      path: "channels",
      label: "Каналы",
    },
    {
      icon: MdInsights,
      path: "automations",
      label: "Чат-бот (сценарии)",
    },
    ...aiAgentsMenu,
  ];

  const allMenu = [
    { items: adminMenu },
    { items: replyMenu, title: "Ответы клиентам" },
    { items: baggageMenu },
    { items: marketplaceMenu },
    { items: advancedAdminMenu },
  ].map(({ items, title }) => {
    const visible = items.filter((item) => {
      // Inbox — это базовый оперативный экран поддержки, а Аналитика — главная страница.
      // Показываем всегда, даже если их нет в org.modules
      // (дашборд сам скрывает виджеты отключённых модулей).
      if (item.path === "/auth/logout" || item.path.startsWith("orgs/") || item.path === "inbox" || item.path === "stats" || item.path === "channels") {
        return true
      }
      return org?.modules?.includes(item.path)
    })
    // Заголовок группы храним прямо на массиве, чтобы не менять отрисовку меню
    return Object.assign(visible, { title });
  });

  useEffect(() => {
    document.addEventListener("click", (event) => {
      if (!sidebar.current) {
        return;
      }
      if (!sidebar.current.contains(event.target) && !burger.current.contains(event.target)) {
        sidebar.current.classList.remove("active");
      }
    });
  }, []);

  useEffect(() => {
    sidebar.current.classList.remove("active");
  }, [location]);


  return (
    <div className="page-wrapper" id="main-wrapper">
      <aside className="left-sidebar admin-nav" ref={sidebar} style={{ width: sidebarOpen ? 300 : 50 }}>
        {sidebarOpen ? (
          <>
            <div className="admin-logo flex items-center justify-between">
              <div className="flex-1">
                <Logo org={org} />
              </div>
              <Button variant="text" onClick={() => {
                dispatch(updateSidebarAction({ open: !sidebarOpen }));
              }}>
                <FaArrowLeft />
              </Button>
            </div>
            <Box sx={{ width: '100%', maxWidth: 360 }}>
              <nav className="sidebar-nav">
                <List>
                  {allMenu.map((group, groupIndex) => {
                    if (group.length === 0) return null
                    return <div key={groupIndex}>
                      {group.title && (
                        <div className="px-4 pt-1 pb-1 text-xs uppercase tracking-wide text-gray-400">{group.title}</div>
                      )}
                      {group.map((item, index) => {
                        return <>
                          <div key={index} className="menu-item">
                            <ListItemButton component={NavLink} to={item.path} className={`sidebar-link`} id={`${item.path}-item`}>
                              <ListItemIcon sx={{ minWidth: '1.5rem' }}>
                                {item.icon && <item.icon id={item.path} />}
                              </ListItemIcon>
                              <ListItemText primary={item.label}></ListItemText>
                            </ListItemButton>
                            <div className="sub-menu-wrapper" timeout="auto">
                              {item.children && item.children.map((c, subInx) => {
                                return <List component="div" disablePadding className="sub-menu" key={subInx}>
                                  <ListItemButton sx={{ pl: 4 }} component={NavLink} to={c.path} className={`sidebar-link ${c.active ? 'active' : ''}`}>
                                    <ListItemIcon sx={{ minWidth: '1.5rem' }}>
                                      {item.icon && <item.icon />}
                                    </ListItemIcon>
                                    <ListItemText primary={c.label} />
                                  </ListItemButton>
                                  <Collapse in={location.pathname.includes(String(c.path).split('/').slice(0, -1).join('/')) || c.active} timeout="auto">
                                    {c.children && c.children?.filter((c) => !c.disabled).map((cc, subInx) => {
                                      return <List component="div" disablePadding className="sub-menu" key={subInx}>
                                        <ListItemButton sx={{ pl: 4 }} component={NavLink} to={cc.path} style={{ paddingLeft: '4rem' }} className={`sidebar-link ${cc.active ? 'active' : ''}`}>
                                          <ListItemIcon sx={{ minWidth: '1.5rem' }}>
                                            {cc.icon && <cc.icon />}
                                          </ListItemIcon>
                                          <ListItemText primary={cc.label} />
                                        </ListItemButton>
                                      </List>
                                    })}
                                  </Collapse>
                                </List>
                              })}
                            </div>
                          </div>
                        </>
                      })}
                      <Divider style={{ margin: '10px 0' }} />
                    </div>
                  })}
                </List>
                <Divider className="my-2 mt-4" />
                <section className="text-center fs-14 p-16">
                  <a className="color-blue" style={{
                    lineHeight: '2',
                    display: 'block',
                    padding: 0,
                    margin: 0
                  }} href="https://ubee.kz/privacy" target="_blank">Privacy Policy</a>
                  <a className="color-blue" href="https://ubee.kz/terms" target="_blank">Terms of Service</a>
                  <div className="m-t-8">{new Date().getFullYear()}. All rights reserved.</div>
                </section>
              </nav>
            </Box>
          </>
        ) : (
          <div className="sidebar-close p-2">
            <div className="flex justify-center mb-4">
              <Logo org={org} compact />
            </div>
            <FaArrowRight style={{
              width: '100%',
            }} onClick={() => {
              dispatch(updateSidebarAction({ open: !sidebarOpen }));
            }} />
            <section className="mt-8">
              {allMenu.map((item, index) => {
                return <div key={index} className="flex flex-col" style={{ gap: '6px' }}>
                  {item.map((i, iInx) => {
                    return <div key={iInx}>
                      <NavLink to={i.path} className="sidebar-link flex items-center justify-center text-center" style={{ padding: '2px' }}>
                        <ListItemIcon sx={{ minWidth: '1.5rem', height: '1.5rem' }} className="flex items-center justify-center">
                          {i.icon && <i.icon color="primary" />}
                        </ListItemIcon>
                      </NavLink>
                    </div>
                  })}
                </div>
              })}
            </section>
          </div>
        )}
      </aside>
      <section className={`admin-content ${sidebarOpen ? 'opened' : 'closed'}`}>
        <div id="admin-header" className="ml-auto flex justify-between" style={{ padding: "12px 0" }}>
          <div className="flex-1 no-mobile">

          </div>
          <div className="flex">
            <UserMenu
              user={authUser}
              setMobileOpen={() => {
                sidebar.current.classList.toggle("active");
              }}
            />
            <IconButton
              ref={burger}
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => {
                sidebar.current.classList.toggle("active");
              }}
              sx={{ display: { sm: "none" } }}
            >
              <FaBars></FaBars>
            </IconButton>
          </div>
        </div>

        {subscriptionData?.status === "TRIALING" && trialDaysLeft > 0 && (
          <div id="notification" className="py-4">
            <Alert
              severity={trialDaysLeft <= 1 ? "warning" : "info"}
              action={
                <Button color="primary" size="small" onClick={() => navigate("/admin/plans")}>
                  Выбрать тариф
                </Button>
              }
            >
              Пробный период: {trialDaysLeft === 1 ? "остался последний день." : `осталось ${trialDaysLeft} дн.`} В кабинете демо-данные — можно удалять и добавлять свои.
            </Alert>
          </div>
        )}
        {isAILimitReached && (
          <div className="py-2">
            <Alert
              severity="warning"
              action={
                <Button color="primary" size="small" onClick={() => navigate("/admin/plans")}>
                  Выбрать тариф
                </Button>
              }
            >
              ИИ-ответы на этот месяц закончились ({aiUsage.limit.toLocaleString("ru-RU")} по тарифу). ИИ-агент не отвечает клиентам — отвечайте вручную или перейдите на тариф выше.
            </Alert>
          </div>
        )}
        <Outlet />
      </section>
    </div >
  );
};

export default withUser(AdminLayout);
